import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { includes, map, union, pull, kebabCase } from 'lodash';

import BlockSection from '~/components/wrappers/BlockSection';

import ButtonGroup from '~/components/base/ButtonGroup';

import { customGrid } from '~/components/blocks/acf/FullWidthFeature';

import Highlight from '~/components/Highlight';
import ImageCollage from '~/components/ImageCollage';

import {
    GlobalBlockOptions,
    AcfAttributes,
    DefaultGlobalBlockOptions,
    DefaultAcfAttributes,
    AcfLink
} from '~/utilities/propTypes';

const Grid = styled.div`
    ${customGrid};
    row-gap: 10px;
    grid-template-areas: '. content content content image image image';
    grid-template-columns: ${({ theme }) =>
            theme.layout.gutter} 1fr 1fr 1fr 1fr 1fr ${({ theme }) =>
            theme.layout.gutter};

    @media (min-width: ${({ theme }) =>
            theme.breakpoint.tablet}) and (max-width: ${({ theme }) =>
            theme.breakpointNumbers.desktop - 1}px) {
        grid-template-columns: ${({ theme }) =>
                theme.layout.gutter} 1fr 1fr 0.25fr 1fr 1fr ${({ theme }) =>
                theme.layout.gutter};
        grid-template-areas: '. content content . image image image';
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        grid-template-areas: '. content content . image image image';
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        grid-template-areas: '. . content . image image image';
    }
`;

const Content = styled.div`
    grid-area: content;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        padding: 7vw 0;
    }
`;

const ContentInner = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    position: sticky;
    width: 100%;
    left: 0;
    top: 0;
    padding: 50px 0;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
        min-height: 650px;
        padding: 0;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.largeTablet}) {
        min-height: 750px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        height: 100vh;
    }
`;

const CollageWrapper = styled.div`
    grid-area: image;

    @media (min-width: ${({ theme }) => theme.breakpoint.desktop}) {
        height: 100vh;
        position: sticky;
        top: 0;
        margin-left: 35px;
    }

    @media (min-width: ${({ theme }) => theme.breakpoint.headerBreak}) {
        margin-left: 70px;
    }
`;

const BrandFeature = ({
    images,
    highlights,
    ctas = [],
    globalBlockOptions = DefaultGlobalBlockOptions,
    attributes = DefaultAcfAttributes
}) => {
    const [itemsInView, setItemsInView] = useState([]);

    const updateInView = useCallback(
        (id, inView) => {
            setItemsInView((itemsInView) => {
                let updatedItems = [];
                if (inView) {
                    updatedItems = union(itemsInView, [id]);
                } else if (includes(itemsInView, id)) {
                    updatedItems = pull(itemsInView, id);
                }
                return updatedItems;
            });
        },
        [setItemsInView]
    );

    return (
        <BlockSection {...{ globalBlockOptions, attributes }} padding="none">
            <Grid>
                <Content>
                    <ContentInner>
                        {highlights.map(({ highlight }) => (
                            <Highlight
                                key={kebabCase(highlight)}
                                {...{ highlight, updateInView, itemsInView }}
                            />
                        ))}
                        {ctas?.length ? (
                            <div>
                                <ButtonGroup
                                    links={map(ctas, 'link')}
                                    marginTop="small"
                                    justify="flex-start"
                                />
                            </div>
                        ) : null}
                    </ContentInner>
                </Content>
                <CollageWrapper>
                    <ImageCollage {...{ images }} />
                </CollageWrapper>
            </Grid>
        </BlockSection>
    );
};

BrandFeature.propTypes = {
    images: PropTypes.arrayOf(
        PropTypes.shape({
            image: PropTypes.shape({}).isRequired
        })
    ).isRequired,
    highlights: PropTypes.arrayOf(
        PropTypes.shape({
            highlight: PropTypes.string.isRequired
        })
    ).isRequired,
    ctas: PropTypes.arrayOf(
        PropTypes.shape({
            link: AcfLink
        })
    ),
    globalBlockOptions: GlobalBlockOptions,
    attributes: AcfAttributes
};

export default BrandFeature;
